import type { GeoJSONFeature, Map as MapboxMap } from "mapbox-gl";
import * as SunCalc from "suncalc";
import {
  calculateAnnualSunHoursWithSimshady,
  generateLocationShadeProfile,
  runSimshadyVisualization,
  type ShadeProfileOptions,
  type ShadeProfileResult,
} from "./shadeProfile";
import { getFeaturesOrigin, sunCalcToMapboxDirection } from "./geometryUtils";
import SimShadyMeshLayer from "./SimShadyMeshLayer";

export interface SimShadyShadowLayerOptions {
  date?: Date;
  color?: string;
  opacity?: number;
  getFeatures?: () => Promise<GeoJSONFeature[]>;
  debug?: (message: string) => void;
  onProgress?: (progress: number | null) => void;
  setShadingOverlayActive?: (active: boolean) => void;
}

interface SunExposureOptions {
  startDate: Date;
  endDate: Date;
}

export class SimShadyShadowLayer {
  private map: MapboxMap | null = null;
  readonly meshLayer = new SimShadyMeshLayer();
  private date: Date;
  private visible = true;
  private sunExposureEnabled = false;
  private sunExposureRange: SunExposureOptions | null = null;
  private getFeatures?: () => Promise<GeoJSONFeature[]>;
  private debug?: (message: string) => void;
  private featureCache: GeoJSONFeature[] = [];
  private sunHoursCache = new Map<string, number>();
  private profileCache = new Map<string, ShadeProfileResult>();
  private visualizationDone = false;
  private onProgress?: (progress: number | null) => void;
  private setShadingOverlayActive?: (active: boolean) => void;

  constructor(options: SimShadyShadowLayerOptions = {}) {
    this.date = options.date ?? new Date();
    this.getFeatures = options.getFeatures;
    this.debug = options.debug;
    this.onProgress = options.onProgress;
    this.setShadingOverlayActive = options.setShadingOverlayActive;
  }

  addTo(map: MapboxMap): this {
    this.map = map;
    this.meshLayer.addTo(map);
    this.applyLights();
    void this.refreshFeatures().then(() => this.runVisualizationOnce());
    return this;
  }

  remove(): void {
    if (!this.map) return;
    this.map.setLights([]);
    this.meshLayer.remove();
    this.setShadingOverlayActive?.(false);
    this.onProgress?.(null);
    this.map = null;
    this.featureCache = [];
    this.sunHoursCache.clear();
    this.profileCache.clear();
    this.visualizationDone = false;
  }

  setDate(date: Date): void {
    this.date = date;
    this.applyLights();
  }

  setSunExposure(enabled: boolean, range?: SunExposureOptions): void {
    this.sunExposureEnabled = enabled;
    this.sunExposureRange = enabled && range ? range : null;
    this.applyLights();
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.meshLayer.setVisible(visible);
    this.applyLights();

    if (!visible) {
      this.setShadingOverlayActive?.(false);
    } else if (this.meshLayer.hasMesh()) {
      this.setShadingOverlayActive?.(true);
    }
  }

  private async runVisualizationOnce(): Promise<void> {
    if (!this.map || !this.visible || this.visualizationDone) return;

    this.visualizationDone = true;
    this.onProgress?.(0);
    try {
      const features = await this.loadFeatures();
      const center = this.map.getCenter();
      const origin = getFeaturesOrigin(features) ?? {
        lng: center.lng,
        lat: center.lat,
      };

      await runSimshadyVisualization(features, origin, {
        meshLayer: this.meshLayer,
        onProgress: (percent) => this.onProgress?.(percent),
      });

      if (this.meshLayer.hasMesh()) {
        this.setShadingOverlayActive?.(true);
      }
      this.map.triggerRepaint();
    } catch (error) {
      this.visualizationDone = false;
      this.setShadingOverlayActive?.(false);
      this.debug?.(`Failed to run simshady visualization: ${String(error)}`);
    } finally {
      this.onProgress?.(null);
    }
  }

  async getShadingFeatures(): Promise<GeoJSONFeature[]> {
    return this.loadFeatures();
  }

  getHoursOfSun(x: number, y: number): number {
    if (!this.map) return 0;

    const lngLat = this.map.unproject([x, y]);
    const cacheKey = `${lngLat.lat.toFixed(5)}:${lngLat.lng.toFixed(5)}`;
    const cached = this.sunHoursCache.get(cacheKey);
    if (cached !== undefined) return cached;

    void this.computeAndCacheSunHours(lngLat.lat, lngLat.lng, cacheKey);
    return 0;
  }

  async _generateLocationShadeProfile(
    options: ShadeProfileOptions,
  ): Promise<ShadeProfileResult> {
    const features = await this.loadFeatures();
    const cacheKey = [
      options.location.lat.toFixed(5),
      options.location.lng.toFixed(5),
      options.startDate.toISOString(),
      options.endDate.toISOString(),
    ].join("|");

    const cached = this.profileCache.get(cacheKey);
    if (cached) return cached;

    const profile = await generateLocationShadeProfile(features, options);
    this.profileCache.set(cacheKey, profile);
    return profile;
  }

  private async computeAndCacheSunHours(
    lat: number,
    lng: number,
    cacheKey: string,
  ) {
    try {
      const features = await this.loadFeatures();
      const hours = await calculateAnnualSunHoursWithSimshady(features, {
        lat,
        lng,
      });
      this.sunHoursCache.set(cacheKey, hours);
    } catch (error) {
      this.debug?.(`Failed to compute sun hours: ${String(error)}`);
    }
  }

  private async refreshFeatures() {
    try {
      this.featureCache = await this.loadFeatures();
    } catch (error) {
      this.debug?.(`Failed to refresh shading features: ${String(error)}`);
    }
  }

  private async loadFeatures(): Promise<GeoJSONFeature[]> {
    if (!this.getFeatures) return this.featureCache;
    const features = await this.getFeatures();
    this.featureCache = features;
    return features;
  }

  private applyLights() {
    if (!this.map) return;

    if (!this.visible) {
      this.map.setLights([]);
      return;
    }

    const center = this.map.getCenter();
    const lightDate =
      this.sunExposureEnabled && this.sunExposureRange
        ? new Date(
            (this.sunExposureRange.startDate.getTime() +
              this.sunExposureRange.endDate.getTime()) /
              2,
          )
        : this.date;

    const sunPosition = SunCalc.getPosition(lightDate, center.lat, center.lng);
    const direction = sunCalcToMapboxDirection(
      sunPosition.altitude,
      sunPosition.azimuth,
    );

    this.map.setLights([
      {
        id: "ambient",
        type: "ambient",
        properties: {
          intensity: sunPosition.altitude > 0 ? 0.35 : 0.15,
        },
      },
      {
        id: "directional",
        type: "directional",
        properties: {
          intensity: sunPosition.altitude > 0 ? 0.85 : 0.1,
          direction,
          "cast-shadows": sunPosition.altitude > 0,
          "shadow-intensity": 0.85,
        },
      },
    ]);
  }
}

export default SimShadyShadowLayer;
